/* eslint-disable camelcase */
import { ElasticQuery } from "types/gql-types";
import { userLoginToken } from "utils/general-utils";

const KEY = "exportFields";

export const lastExportFields = (listId: string): string[] => {
  const value = localStorage.getItem(`${KEY}:${listId}`);
  if (value) {
    return JSON.parse(value);
  }
  return [];
};

export const saveLastExportFields = (listId: string, fields: string[]) : void => {
  localStorage.setItem(`${KEY}:${listId}`, JSON.stringify(fields));
};

type DownloadCsvOpts = {
  columns: string[];
  interestListIds: string[];
}

type SelectionQuery = {
  elastic_query: ElasticQuery | null,
  interest_ids: string[] | null
}

type DownloadRequestArgs = {
  url: string;
  token: string | null;
  fileName: string;
  opts: DownloadCsvOpts;
  selectedPageRange: {from: number | null, to: number | null};
  selection: SelectionQuery;
};

// Shared by downloadCsv/downloadXlsx below — both hit the same request shape and the
// same anchor-click download mechanics, differing only in URL, filename extension,
// error message, and how the response body is read (text vs. blob).
const postDownloadRequest = ({
  url, token, fileName, opts, selectedPageRange, selection,
}: DownloadRequestArgs): Promise<Response> => {
  const { from, to } = selectedPageRange;

  return fetch(url, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "authorization": `Bearer ${token}`,
      "filename": fileName,
    },
    body: JSON.stringify({
      "columns": opts.columns,
      "interest_list_ids": opts.interestListIds,
      ...selection,
      "page_from": from,
      "page_to": to,
      // eslint-disable-next-line new-cap
      "tz": Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });
};

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("style", "visibility:hidden");
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadCsv =
async (
  opts: DownloadCsvOpts,
  selectedPageRange: {from: number | null, to: number | null},
  selection: SelectionQuery,
): Promise<void> => {
  const token = userLoginToken();
  const fileName = `export-${Date.now()}.csv`;

  const res = await postDownloadRequest({
    url: "/api/interests/dl/spreadsheet", token, fileName, opts, selectedPageRange, selection,
  });

  if (res.status !== 200) throw new Error("Error downloading CSV");

  const csvString = await res.text();

  triggerBlobDownload(new Blob([csvString], { type: "text/csv" }), fileName);
};

export const downloadXlsx =
async (
  opts: DownloadCsvOpts,
  selectedPageRange: {from: number | null, to: number | null},
  selection: SelectionQuery,
): Promise<void> => {
  const token = userLoginToken();
  const fileName = `export-${Date.now()}.xlsx`;

  const res = await postDownloadRequest({
    url: "/api/interests/dl/spreadsheet/xlsx", token, fileName, opts, selectedPageRange, selection,
  });

  if (res.status !== 200) throw new Error("Error downloading XLSX");

  // binary payload -> blob, not text() (which would corrupt it via UTF-8 decoding)
  const xlsxBlobData = await res.blob();

  triggerBlobDownload(
    new Blob([xlsxBlobData], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    fileName,
  );
};
